import React from "react";
import { createPortal } from "react-dom";
import { ApiError, cockpit } from "../api";
import type {
  DeliveryResolutionResult,
  RequestDetail,
  UncertainDeliveryAttempt,
} from "../api/types";
import { Banner, Button, Card, Dialog, StatusPill } from "../ds";
import { useLiveInvalidation } from "../live/live-provider";
import { useT } from "../i18n";
import { Icon, fmtStamp } from "../lib";

type ResolutionChoice = "delivered" | "not-delivered";

export function deliveryAuditAttemptIdentity(
  value: unknown,
): { attemptId: string; draftId: string } | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const payload = value as Record<string, unknown>;
  return typeof payload.attemptId === "string" && typeof payload.draftId === "string"
    ? { attemptId: payload.attemptId, draftId: payload.draftId }
    : null;
}

export function activeDraftUncertainDelivery(
  data: RequestDetail,
): UncertainDeliveryAttempt | null {
  const { delivery, draft, request, messages } = data;
  if (
    delivery?.state !== "UNKNOWN"
    || !delivery.attemptId
    || !delivery.draft_id
  ) {
    return null;
  }
  const originatingMessage = messages.find((message) => message.direction === "inbound");
  return {
    attemptId: delivery.attemptId,
    state: "UNKNOWN",
    attemptNumber: delivery.attemptNumber ?? 1,
    errorCode: delivery.errorCode ?? null,
    startedAt: delivery.startedAt ?? null,
    finishedAt: delivery.finishedAt ?? null,
    smtpMessageId: delivery.smtpMessageId ?? null,
    providerMessageId: delivery.providerMessageId ?? null,
    draftId: delivery.draft_id,
    requestId: request.id,
    requestNo: request.request_no ?? null,
    reference: request.reference ?? null,
    subject: delivery.draft_id === draft?.id ? draft.subject : originatingMessage?.subject ?? null,
    recipient: delivery.recipientAddr ?? null,
    isCurrentDraft: delivery.draft_id === draft?.id,
  };
}

export interface DeliveryResolutionActionsProps {
  attempt: UncertainDeliveryAttempt;
  onResolved: (result: DeliveryResolutionResult) => void;
  onStale?: () => void | Promise<void>;
}

export function DeliveryResolutionActions({
  attempt,
  onResolved,
  onStale,
}: DeliveryResolutionActionsProps) {
  const t = useT();
  const [choice, setChoice] = React.useState<ResolutionChoice | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [errorKey, setErrorKey] = React.useState<string | null>(null);

  const begin = (next: ResolutionChoice) => {
    if (busy) return;
    setErrorKey(null);
    setChoice(next);
  };

  const confirm = async () => {
    if (!choice || busy) return;
    const delivered = choice === "delivered";
    setBusy(true);
    setErrorKey(null);
    try {
      const result = await cockpit.resolveUncertainDelivery(
        attempt.attemptId,
        delivered,
      );
      setChoice(null);
      onResolved(result);
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        setChoice(null);
        setErrorKey("delivery.error.stale");
        await onStale?.();
      } else {
        setErrorKey("delivery.error.resolve");
      }
    } finally {
      setBusy(false);
    }
  };

  const delivered = choice === "delivered";
  const choiceName = delivered ? "delivered" : "not-delivered";

  return (
    <div className="delivery-resolution-actions">
      <div className="delivery-resolution-actions__buttons">
        <Button
          variant="primary"
          size="sm"
          disabled={busy}
          data-action="delivery-mark-delivered"
          iconLeft={<Icon name="checkCircle" size={15} />}
          onClick={() => begin("delivered")}
        >
          {t("delivery.action.delivered")}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={busy}
          data-action="delivery-mark-not-delivered"
          iconLeft={<Icon name="xCircle" size={15} />}
          onClick={() => begin("not-delivered")}
        >
          {t("delivery.action.notDelivered")}
        </Button>
      </div>

      {errorKey && (
        <div
          className="delivery-resolution-actions__error"
          data-testid="delivery-resolution-error"
          role="status"
        >
          {t(errorKey)}
        </div>
      )}

      <Dialog
        open={choice !== null}
        onClose={busy ? undefined : () => setChoice(null)}
        tone="warning"
        icon={<Icon name={delivered ? "checkCircle" : "xCircle"} size={20} />}
        title={t(delivered
          ? "delivery.confirm.deliveredTitle"
          : "delivery.confirm.notDeliveredTitle")}
        footer={<>
          <Button variant="ghost" disabled={busy} onClick={() => setChoice(null)}>
            {t("common.cancel")}
          </Button>
          <Button
            variant={delivered ? "primary" : "destructive"}
            loading={busy}
            disabled={busy}
            data-action={`delivery-confirm-${choiceName}`}
            onClick={() => { void confirm(); }}
          >
            {t(delivered
              ? "delivery.action.confirmDelivered"
              : "delivery.action.confirmNotDelivered")}
          </Button>
        </>}
      >
        <div className="stack-sm">
          <p className="delivery-confirm__attempt">
            {t("delivery.confirm.attempt", { id: attempt.attemptId })}
          </p>
          <p>
            {t(delivered
              ? "delivery.confirm.deliveredBody"
              : "delivery.confirm.notDeliveredBody")}
          </p>
        </div>
      </Dialog>
    </div>
  );
}

function DeliveryAttemptContext({ attempt }: { attempt: UncertainDeliveryAttempt }) {
  const t = useT();
  return (
    <dl className="delivery-reconcile__meta">
      <DeliveryMeta label={t("delivery.recipient")} value={attempt.recipient || t("common.notSet")} />
      <DeliveryMeta label={t("delivery.attemptNumber")} value={String(attempt.attemptNumber)} />
      <DeliveryMeta label={t("delivery.attemptedAt")} value={attempt.startedAt ? fmtStamp(attempt.startedAt) : t("common.notSet")} />
      <DeliveryMeta label={t("delivery.completedAt")} value={attempt.finishedAt ? fmtStamp(attempt.finishedAt) : t("common.notSet")} />
      <DeliveryMeta label={t("delivery.errorCode")} value={attempt.errorCode || t("common.notSet")} mono />
      <DeliveryMeta label={t("delivery.smtpMessageId")} value={attempt.smtpMessageId || t("delivery.messageIdMissing")} mono />
      <DeliveryMeta label={t("delivery.providerMessageId")} value={attempt.providerMessageId || t("delivery.messageIdMissing")} mono />
      <DeliveryMeta label={t("delivery.attemptId")} value={attempt.attemptId} mono />
      <DeliveryMeta label={t("delivery.draftId")} value={attempt.draftId} mono />
    </dl>
  );
}

export function DeliveryReconciliationPanel({
  attempt,
  onResolved,
  onStale,
}: DeliveryResolutionActionsProps) {
  const t = useT();
  return (
    <div className="delivery-reconcile delivery-reconcile--workspace" data-testid="delivery-reconciliation-workspace">
      <Card
        title={t("delivery.queue.title")}
        subtitle={t("delivery.queue.subtitle")}
        actions={<StatusPill size="sm" tone="warning" label={t(attempt.isCurrentDraft
          ? "delivery.currentDraft" : "delivery.historicalDraft")} />}
      >
        <Banner tone="warning" icon={<Icon name="help" size={18} />}>
          {t("delivery.queue.verifyInstructions")}
        </Banner>
        <DeliveryAttemptContext attempt={attempt} />
        <div className="delivery-reconcile__footer delivery-reconcile__footer--actions-only">
          <DeliveryResolutionActions
            attempt={attempt}
            onResolved={onResolved}
            onStale={onStale}
          />
        </div>
      </Card>
    </div>
  );
}

export interface DeliveryReconciliationQueueProps {
  onOpenRequest?: (requestRef: string) => void;
  /** Inbox keeps attention above the worklist and moves only calm states into its footer. */
  splitInboxPlacement?: boolean;
  /** Narrow server-backed Inbox facts that remain visible while delivery needs attention. */
  inboxFooterFacts?: React.ReactNode;
  inboxFooterAriaLabel?: string;
  /** Shown at the end of that footer, after the facts. */
  inboxFooterAction?: React.ReactNode;
  /**
   * Where the footer is rendered instead of in place. The check sits above the page's content and
   * the footer is the page's last row, so in place it would be read and focused before the content.
   * `null` while the host is not mounted: nothing is rendered rather than the footer in the wrong place.
   */
  inboxFooterHost?: HTMLElement | null;
}

export function DeliveryReconciliationQueue({
  onOpenRequest,
  splitInboxPlacement = false,
  inboxFooterFacts,
  inboxFooterAriaLabel,
  inboxFooterAction,
  inboxFooterHost,
}: DeliveryReconciliationQueueProps) {
  const t = useT();
  const [items, setItems] = React.useState<UncertainDeliveryAttempt[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);
  const [noticeKey, setNoticeKey] = React.useState<string | null>(null);
  const requestSequence = React.useRef(0);

  const load = React.useCallback(async () => {
    const sequence = requestSequence.current + 1;
    requestSequence.current = sequence;
    setLoading(true);
    setError(false);
    try {
      const next = await cockpit.listUncertainDeliveries();
      if (requestSequence.current === sequence) setItems(next);
    } catch {
      if (requestSequence.current === sequence) setError(true);
    } finally {
      if (requestSequence.current === sequence) setLoading(false);
    }
  }, []);

  // Send attempts live in the workspace domain (#544).
  const invalidations = useLiveInvalidation("workspace");
  React.useEffect(() => {
    void load();
    return () => { requestSequence.current += 1; };
  }, [invalidations, load]);

  const openRequest = (item: UncertainDeliveryAttempt) => {
    onOpenRequest?.(String(item.requestNo ?? item.requestId));
  };

  const resolved = (result: DeliveryResolutionResult) => {
    setItems((current) => current.filter((item) => item.attemptId !== result.attemptId));
    setNoticeKey(result.resolved === "delivered"
      ? "delivery.success.delivered"
      : "delivery.success.notDelivered");
  };

  const stale = async () => {
    setNoticeKey("delivery.error.stale");
    await load();
  };

  const place = (content: React.ReactNode, attention: boolean) => {
    if (!splitInboxPlacement) return content;
    const footer = (calmDeliveryState?: React.ReactNode) => {
      const element = (
        <footer
          className="inbox-system-footer inbox-delivery-slot inbox-delivery-slot--clear"
          data-testid="inbox-system-footer"
          aria-label={inboxFooterAriaLabel}
        >
          <div className="inbox-system-footer__facts">
            {inboxFooterFacts}
            {calmDeliveryState}
          </div>
          {inboxFooterAction}
        </footer>
      );
      if (inboxFooterHost === undefined) return element;
      return inboxFooterHost ? createPortal(element, inboxFooterHost) : null;
    };
    return attention ? <>
      <div className="inbox-delivery-slot inbox-delivery-slot--attention">{content}</div>
      {inboxFooterFacts ? footer() : null}
    </> : footer(content);
  };

  // #1543: in the Inbox the check waits in the footer. Above the worklist it pushed the rows down
  // and, once nothing was unclear, pulled them back up on every load.
  if (loading && items.length === 0) {
    return place(
      <div
        className="delivery-reconcile-loading"
        data-testid="delivery-reconciliation-loading"
        role="status"
      >
        {t("delivery.queue.loading")}
      </div>,
      false,
    );
  }

  if (error && items.length === 0) {
    return place(
      <div data-testid="delivery-reconciliation-error" className="delivery-reconcile-error">
        <Banner tone="danger" title={t("delivery.error.load")}>
          <Button
            variant="secondary"
            size="sm"
            data-action="delivery-reconciliation-retry"
            onClick={() => { void load(); }}
          >
            {t("common.retry")}
          </Button>
        </Banner>
      </div>,
      true,
    );
  }

  if (items.length === 0) {
    if (noticeKey && !noticeKey.startsWith("delivery.success")) {
      return place(
        <div className="delivery-reconcile-notice" role="status">
          <Banner tone="warning">{t(noticeKey)}</Banner>
        </div>,
        true,
      );
    }
    const clearState = noticeKey ? (
      <div className="delivery-reconcile-notice inbox-status-fact inbox-status-fact--on" role="status">
        <span className="inbox-status-fact__dot" aria-hidden="true" />
        <span>{t(noticeKey)}</span>
      </div>
    ) : (
      <div
        className="delivery-reconcile-notice inbox-status-fact inbox-status-fact--on"
        data-testid="delivery-reconciliation-empty"
        role="status"
      >
        <span className="inbox-status-fact__dot" aria-hidden="true" />
        <span>{t("delivery.queue.empty")}</span>
      </div>
    );
    return place(clearState, false);
  }

  return place(
    <div data-testid="delivery-reconciliation-queue" className="delivery-reconcile">
      <Card
        title={t("delivery.queue.title")}
        subtitle={t("delivery.queue.subtitle")}
        actions={<StatusPill
          size="sm"
          tone="warning"
          label={t("delivery.queue.count", { n: items.length })}
        />}
      >
        <Banner tone="warning" icon={<Icon name="help" size={18} />}>
          {t("delivery.queue.verifyInstructions")}
        </Banner>
        {noticeKey && (
          <div className="delivery-reconcile__notice" role="status">
            {t(noticeKey)}
          </div>
        )}
        <div className="delivery-reconcile__list">
          {items.map((item) => (
            <article className="delivery-reconcile__item" key={item.attemptId}>
              <div className="delivery-reconcile__head">
                <div>
                  <div className="delivery-reconcile__request">
                    {item.reference || (item.requestNo != null ? `#${item.requestNo}` : item.requestId)}
                  </div>
                  <div className="delivery-reconcile__subject">
                    {item.subject || t("common.noSubject")}
                  </div>
                </div>
                <StatusPill
                  size="sm"
                  tone={item.isCurrentDraft ? "warning" : "neutral"}
                  label={t(item.isCurrentDraft
                    ? "delivery.currentDraft"
                    : "delivery.historicalDraft")}
                />
              </div>

              <DeliveryAttemptContext attempt={item} />

              <div className="delivery-reconcile__footer">
                <Button
                  variant="ghost"
                  size="sm"
                  data-action="delivery-open-request"
                  iconLeft={<Icon name="mail" size={15} />}
                  onClick={() => openRequest(item)}
                >
                  {t("delivery.openRequest")}
                </Button>
                <DeliveryResolutionActions
                  attempt={item}
                  onResolved={resolved}
                  onStale={stale}
                />
              </div>
            </article>
          ))}
        </div>
      </Card>
    </div>,
    true,
  );
}

function DeliveryMeta({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div>
      <dt>{label}</dt>
      <dd className={mono ? "neo-mono" : undefined}>{value}</dd>
    </div>
  );
}
